/* eslint-disable no-unused-vars */
import { Picker, init } from 'emoji-mart';
import data, { EmojiMartData } from '@emoji-mart/data';

type Props = {
  triggerElement: HTMLElement;
  position?: 'center' | 'baseline' | 'bottom';
  actionOnEmojiSelect: (emoji: string) => void;
  actionOnTriggerClick?: (isOpen: boolean) => void;
  changeTriggerElement?: (TriggerElement: HTMLElement) => void;
};

export class EmojiPicker {
  static instance: EmojiPicker | null = null;
  private picker: Picker | null = null;
  private isOpen: boolean = false;
  private triggerElement: HTMLElement | null = null;
  private actionOnEmojiSelect: (data: any) => void;
  private actionOnTriggerClick?: (isOpen: boolean) => void;
  parentElement: HTMLElement | null = null;
  private handleEscapeKey: (event: KeyboardEvent) => void;
  private overlayElement: HTMLElement | null = null;

  private constructor(triggerElement: HTMLElement, exceptEmojis?: string[]) {
    exceptEmojis?.forEach(emoji => {
      delete (data as EmojiMartData)?.emojis[emoji];
    });
    init({ data });
    this.triggerElement = triggerElement;
    this.parentElement = document.getElementById('emojiPickerContainer');
    this.init(triggerElement, exceptEmojis);

    this.handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.closePicker();
      }
    };
  }

  private init(triggerElement: HTMLElement, exceptEmojis?: string[]) {
    const PickerElement = window?.customElements.get('em-emoji-picker') as unknown as typeof Picker;

    this.triggerElement = triggerElement;
    this.hidePicker();
    this.picker = new (PickerElement || Picker)({
      parent: this.parentElement,
      autoFocus: true,
      exceptEmojis: exceptEmojis,
      onEmojiSelect: (e: any) => {
        if (this.actionOnEmojiSelect) {
          this.actionOnEmojiSelect({ emoji: e.native });
        }
        this.closePicker();
      },
      onClickOutside: (e: MouseEvent) => {
        const isTrigger = this.triggerElement?.dataset.trigger === 'emojiPicker';
        const position = this.triggerElement?.dataset.triggerPosition;

        if (isTrigger && (e.target === this.triggerElement || this.triggerElement?.contains(e.target as Node))) {
          return this.getTriggerPositionAndSetToContainer(
            this.triggerElement,
            this.parentElement,
            position ? position : 'center'
          );
        }

        this.closePicker();
      }
    });
    this.overlayElement = this.createOverlay();
  }

  public static getInstance(triggerElement: HTMLElement, exceptEmojis?: string[]): EmojiPicker {
    if (EmojiPicker.instance) {
      EmojiPicker.instance.destroy();
    }
    EmojiPicker.instance = new EmojiPicker(triggerElement, exceptEmojis);
    return EmojiPicker.instance;
  }

  public openPicker = (props: Props) => {
    this.isOpen = true;
    this.showPicker(props);
    this.actionOnEmojiSelect = props.actionOnEmojiSelect;
    this.actionOnTriggerClick = props.actionOnTriggerClick;
    this.actionOnTriggerClick && this.actionOnTriggerClick(this.isOpen);
    document.addEventListener('keydown', this.handleEscapeKey);
    window.addEventListener('resize', () => this.handleResize(props));
  };

  public closePicker = () => {
    this.isOpen = false;
    this.hidePicker();
    this.actionOnEmojiSelect = () => {
      return;
    };
    this.actionOnTriggerClick && this.actionOnTriggerClick(this.isOpen);
    document.removeEventListener('keydown', this.handleEscapeKey);
    window.removeEventListener('resize', () => this.handleResize());
    this.destroy();
  };

  public togglePicker = (props: Props) => {
    if (this.isOpen) {
      this.closePicker();
    }
    EmojiPicker.getInstance(props.triggerElement).openPicker(props);
  };

  public getIsOpen = (): boolean => {
    return this.isOpen;
  };

  private hidePicker(): void {
    if (this.parentElement) {
      this.parentElement.style.display = 'none';
    }
    if (this.overlayElement) {
      this.overlayElement.style.display = 'none';
    }
  }

  private showPicker(props: Props): void {
    if (this.parentElement) {
      this.getTriggerPositionAndSetToContainer(this.triggerElement, this.parentElement, props.position);
      if (this.overlayElement) {
        this.overlayElement.style.display = 'block';
      }
    }
  }

  private getTriggerPositionAndSetToContainer = (
    triggerElement: HTMLElement | null,
    emojiPickerContainer: HTMLElement | null,
    vertical: string = 'center'
  ) => {
    if (!triggerElement || !emojiPickerContainer) {
      return;
    }

    const triggerElementRect = triggerElement.getBoundingClientRect();
    const emojiPickerRect = { width: 352, height: 435 };
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

    let top;
    let left;

    left = triggerElementRect.left - emojiPickerRect.width;
    if (vertical === 'baseline') {
      top = triggerElementRect.bottom - emojiPickerRect.height - 40;
    } else if (vertical === 'bottom') {
      top = triggerElementRect.bottom + 25;
      left = triggerElementRect.left;
    } else {
      top = Math.max(0, triggerElementRect.top + (triggerElementRect.height - emojiPickerRect.height) / 2);
    }

    if (top + emojiPickerRect.height > viewportHeight) {
      top = viewportHeight - emojiPickerRect.height;
    }

    if (top < 0) {
      top = 0;
    }

    emojiPickerContainer.style.left = `${left}px`;
    emojiPickerContainer.style.top = `${top}px`;
    emojiPickerContainer.style.position = 'absolute';
    emojiPickerContainer.style.display = 'block';
  };

  private destroy() {
    if (this.picker) {
      this.picker = null;
    }
    if (this.parentElement) {
      this.parentElement.innerHTML = '';
    }
    this.isOpen = false;
    this.triggerElement = null;
    this.actionOnEmojiSelect = () => {
      return;
    };

    if (this.overlayElement) {
      this.overlayElement.style.display = 'none';
    }
  }

  private createOverlay(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'transparent';
    overlay.style.zIndex = '1';
    overlay.style.display = 'none';
    this.parentElement?.appendChild(overlay);
    return overlay;
  }

  handleResize = (props?: Props) => {
    this.getTriggerPositionAndSetToContainer(this.triggerElement, this.parentElement, props?.position);
  };
}

export default EmojiPicker;
